import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import {
  Pencil, X, Check, Trash2, Bold, Italic,
  Strikethrough, List, Variable, Plus,
} from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

const MAX_SECTIONS = 10;
const MAX_TOTAL_ROWS = 10;
const MAX_SECTION_TITLE = 24;
const MAX_ROW_TITLE = 24;
const MAX_ROW_DESC = 72;
const MAX_BODY_LEN = 1024;
const MAX_HEADER_LEN = 60;
const MAX_FOOTER_LEN = 60;

/* ── Collapsed WhatsApp-style preview ─────────────────────────── */
const CollapsedView = ({ data, onEdit }) => {
  const bodyText = data.body?.text || "";
  const listLabel = data.listLabel || "Select an option";
  const sections = data.listSections || [];
  const listFromVariable = data.listFromVariable || "";

  let displayBody = bodyText
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
    .replace(/_(.*?)_/g, "<em>$1</em>")
    .replace(/~(.*?)~/g, "<del>$1</del>");

  return (
    <div className="relative rounded-xl overflow-hidden" style={{ backgroundColor: "#EFEAE2", padding: "10px 10px 6px", minWidth: "260px", maxWidth: "300px" }}>
      <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg" title="Edit">
        <Pencil size={12} className="text-teal-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm relative">
        <div className="absolute -left-[6px] top-0 w-3 h-3 overflow-hidden">
          <div className="absolute bg-white w-3 h-3 transform rotate-45 translate-x-[6px] shadow-sm" />
        </div>
        <div className="px-2 py-1.5">
          {data.header?.format === "TEXT" && data.header?.text && (
            <p className="text-[12px] font-semibold text-[#111B21] mb-0.5 leading-[17px]">{data.header.text.length > 60 ? data.header.text.substring(0, 60) + "..." : data.header.text}</p>
          )}
          {displayBody ? (
            <div className="text-[12px] text-[#111B21] whitespace-pre-wrap break-words leading-[17px]" dangerouslySetInnerHTML={{ __html: displayBody.length > 120 ? displayBody.substring(0, 120) + "..." : displayBody }} />
          ) : (
            <p className="text-[12px] text-gray-400 italic">Type a message...</p>
          )}
          {data.footer?.text && <p className="text-[10px] text-[#667781] mt-1">{data.footer.text}</p>}
          <div className="flex items-center justify-end mt-0.5 -mb-0.5">
            <span className="text-[9px] text-[#667781]">{new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}</span>
          </div>
        </div>

        {/* List CTA button */}
        <div className="border-t border-gray-100">
          <div className="flex items-center justify-center gap-1.5 py-[7px]">
            <List size={12} className="text-[#027EB5]" />
            <span className="text-[12px] font-medium text-[#027EB5]">{listLabel}</span>
          </div>
        </div>
      </div>

      {/* Show section/row preview if manual */}
      {!listFromVariable && sections.length > 0 && (
        <div className="mt-1.5 space-y-1">
          {sections.map((sec, si) => (
            <div key={si} className="bg-white/80 rounded px-2 py-1">
              {sec.title && <p className="text-[8px] font-bold text-teal-600 uppercase">{sec.title}</p>}
              {(sec.rows || []).map((row, ri) => (
                <div key={ri} className="flex items-center justify-between">
                  <span className="text-[9px] text-[#111B21]">{row.title || "Option"}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {listFromVariable && (
        <div className="mt-1.5 px-2 py-1 bg-teal-50 rounded">
          <p className="text-[8px] text-teal-600 font-semibold">Options from variable: {`{{${listFromVariable}}}`}</p>
        </div>
      )}
    </div>
  );
};

/* ── Expanded Editor ──────────────────────────────────────────── */
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [form, setForm] = useState(() => ({
    header: { format: data.header?.format || "NONE", text: data.header?.text || "", mediaUrl: data.header?.mediaUrl || "", fileName: data.header?.fileName || "" },
    body: { text: data.body?.text || "", variables: data.body?.variables ? data.body.variables.map(v => ({ ...v })) : [] },
    footer: { text: data.footer?.text || "" },
    listLabel: data.listLabel || "Select an option",
    listSections: data.listSections ? JSON.parse(JSON.stringify(data.listSections)) : [{ title: "", rows: [{ title: "", description: "" }] }],
    listFromVariable: data.listFromVariable || "",
    listVariableFieldMap: data.listVariableFieldMap || { title: "", description: "" },
    selectionVariable: data.selectionVariable || "",
    useVariable: !!(data.listFromVariable),
  }));
  const [showVarPicker, setShowVarPicker] = useState(false);
  const bodyRef = useRef(null);

  const updateField = (path, value) => {
    setForm(prev => {
      const parts = path.split(".");
      const next = { ...prev };
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) { obj[parts[i]] = { ...obj[parts[i]] }; obj = obj[parts[i]]; }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const insertVariable = (varName) => {
    if (!varName) return;
    if (!form.body.variables.some(v => v.name === varName)) updateField("body.variables", [...form.body.variables, { name: varName, example: "" }]);
    const ta = bodyRef.current;
    const pos = ta?.selectionStart || form.body.text.length;
    updateField("body.text", form.body.text.slice(0, pos) + `{{${varName}}}` + form.body.text.slice(pos));
  };

  const applyFormat = (fmt) => {
    const ta = bodyRef.current; if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    const m = { bold: "*", italic: "_", strikethrough: "~" }[fmt] || "*";
    updateField("body.text", form.body.text.substring(0, s) + `${m}${form.body.text.substring(s, e) || "text"}${m}` + form.body.text.substring(e));
  };

  const totalRows = form.listSections.reduce((sum, sec) => sum + (sec.rows?.length || 0), 0);

  const addSection = () => {
    if (form.listSections.length >= MAX_SECTIONS) return;
    updateField("listSections", [...form.listSections, { title: "", rows: [{ title: "", description: "" }] }]);
  };

  const addRow = (secIdx) => {
    if (totalRows >= MAX_TOTAL_ROWS) return;
    const secs = JSON.parse(JSON.stringify(form.listSections));
    secs[secIdx].rows.push({ title: "", description: "" });
    updateField("listSections", secs);
  };

  const updateSection = (secIdx, field, value) => {
    const secs = JSON.parse(JSON.stringify(form.listSections));
    secs[secIdx][field] = value;
    updateField("listSections", secs);
  };

  const updateRow = (secIdx, rowIdx, field, value) => {
    const secs = JSON.parse(JSON.stringify(form.listSections));
    secs[secIdx].rows[rowIdx][field] = value;
    updateField("listSections", secs);
  };

  const removeRow = (secIdx, rowIdx) => {
    const secs = JSON.parse(JSON.stringify(form.listSections));
    secs[secIdx].rows.splice(rowIdx, 1);
    if (secs[secIdx].rows.length === 0) secs.splice(secIdx, 1);
    updateField("listSections", secs);
  };

  const handleSave = () => {
    const out = { ...form };
    if (form.useVariable) { out.listSections = []; } else { out.listFromVariable = ""; out.listVariableFieldMap = { title: "", description: "" }; }
    delete out.useVariable;
    onSave(out);
  };

  return (
    <div className="nowheel" style={{ backgroundColor: "#EFEAE2", borderRadius: "12px", padding: "14px", minWidth: "380px", maxWidth: "420px" }} onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-teal-500" /><span className="text-[13px] font-bold text-[#111B21]">Interactive List</span></div>
        <div className="flex gap-1.5">
          <button onClick={handleSave} className="px-3 py-1.5 text-[11px] font-bold bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-1 shadow-sm"><Check size={12} />Save</button>
          <button onClick={onCancel} className="px-3 py-1.5 text-[11px] font-bold bg-white text-[#667781] rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">Cancel</button>
        </div>
      </div>

      <NodeEditorTabs nodeType="interactiveList">
      <div className="bg-white rounded-lg shadow-sm mb-3 relative">
        <div className="absolute -left-[6px] top-0 w-3 h-3 overflow-hidden"><div className="absolute bg-white w-3 h-3 transform rotate-45 translate-x-[6px] shadow-sm" /></div>

        {/* Header */}
        <div className="px-3 pt-2.5 pb-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold text-[#667781] uppercase tracking-wider">Header</span>
            <select value={form.header.format} onChange={e => updateField("header.format", e.target.value)} className="text-[10px] font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full border-0 focus:outline-none cursor-pointer">
              <option value="NONE">None</option><option value="TEXT">Text</option>
            </select>
          </div>
          {form.header.format === "TEXT" && <input value={form.header.text} onChange={e => updateField("header.text", e.target.value)} placeholder="Header text..." maxLength={MAX_HEADER_LEN} className="w-full px-0 py-1 text-[13px] font-semibold text-[#111B21] border-0 border-b border-gray-200 focus:border-teal-500 focus:outline-none bg-transparent placeholder:text-gray-400" />}
        </div>

        <div className="mx-3 border-t border-gray-100" />

        {/* Body */}
        <div className="px-3 py-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-[#667781] uppercase tracking-wider">Body</span>
            <div className="flex items-center gap-0.5">
              <button onClick={() => applyFormat("bold")} className="p-1 hover:bg-[#F0F0F0] rounded"><Bold size={11} className="text-[#667781]" /></button>
              <button onClick={() => applyFormat("italic")} className="p-1 hover:bg-[#F0F0F0] rounded"><Italic size={11} className="text-[#667781]" /></button>
              <button onClick={() => applyFormat("strikethrough")} className="p-1 hover:bg-[#F0F0F0] rounded"><Strikethrough size={11} className="text-[#667781]" /></button>
              <div className="w-px h-3 bg-gray-200 mx-0.5" />
              <div className="relative">
                <button onClick={() => setShowVarPicker(!showVarPicker)} className="text-[9px] px-1.5 py-0.5 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 flex items-center gap-0.5"><Variable size={8} />Var</button>
                {showVarPicker && <div className="absolute right-0 top-full mt-1 z-50 w-48"><VariableDropdown value="" onChange={name => { if(name) insertVariable(name); setShowVarPicker(false); }} flowVariables={flowVariables||[]} allowCreate onCreateVariable={onCreateVariable} placeholder="Pick variable..." /></div>}
              </div>
            </div>
          </div>
          <textarea ref={bodyRef} value={form.body.text} onChange={e => updateField("body.text", e.target.value)} placeholder="Type a message..." rows={3} maxLength={MAX_BODY_LEN} className="w-full px-0 py-1 text-[12px] text-[#111B21] border-0 focus:outline-none resize-none bg-transparent placeholder:text-[#8696a0] leading-[17px]" />
          <div className="flex justify-end"><span className="text-[9px] text-[#8696a0]">{form.body.text.length}/{MAX_BODY_LEN}</span></div>
        </div>

        <div className="mx-3 border-t border-gray-100" />

        {/* Footer */}
        <div className="px-3 py-2">
          <span className="text-[10px] font-bold text-[#667781] uppercase tracking-wider">Footer</span>
          <input type="text" value={form.footer.text} onChange={e => updateField("footer.text", e.target.value)} placeholder="Optional footer..." maxLength={MAX_FOOTER_LEN} className="w-full px-0 py-1 text-[11px] text-[#667781] border-0 focus:outline-none bg-transparent placeholder:text-[#8696a0]" />
        </div>
      </div>

      {/* List Label */}
      <div className="mb-3">
        <label className="text-[10px] font-bold text-[#667781] uppercase tracking-wider mb-1 block">List Button Label</label>
        <input type="text" value={form.listLabel} onChange={e => updateField("listLabel", e.target.value)} placeholder="Select an option" maxLength={20} className="w-full px-2 py-1.5 text-[11px] bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-teal-500" />
      </div>

      {/* Toggle: Manual vs Variable */}
      <div className="flex gap-2 mb-3">
        <button onClick={() => updateField("useVariable", false)} className={`flex-1 py-1.5 text-[10px] font-bold rounded-lg transition-colors ${!form.useVariable ? "bg-teal-600 text-white" : "bg-white text-[#667781] border border-gray-200"}`}>Manual Options</button>
        <button onClick={() => updateField("useVariable", true)} className={`flex-1 py-1.5 text-[10px] font-bold rounded-lg transition-colors ${form.useVariable ? "bg-teal-600 text-white" : "bg-white text-[#667781] border border-gray-200"}`}>From Variable</button>
      </div>

      {/* Manual sections */}
      {!form.useVariable && (
        <div className="mb-3 space-y-2">
          {form.listSections.map((sec, si) => (
            <div key={si} className="bg-white rounded-lg shadow-sm p-2.5">
              <input type="text" value={sec.title} onChange={e => updateSection(si, "title", e.target.value)} placeholder={`Section ${si + 1} title (optional)`} maxLength={MAX_SECTION_TITLE} className="w-full px-2 py-1 text-[11px] bg-[#F0F0F0] rounded-lg mb-1.5 border-0 focus:outline-none focus:ring-1 focus:ring-teal-500/30 placeholder:text-[#8696a0] font-semibold" />
              {(sec.rows || []).map((row, ri) => (
                <div key={ri} className="flex gap-1 items-start mb-1">
                  <div className="flex-1 space-y-0.5">
                    <input type="text" value={row.title} onChange={e => updateRow(si, ri, "title", e.target.value)} placeholder="Row title" maxLength={MAX_ROW_TITLE} className="w-full px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none focus:ring-1 focus:ring-teal-500/30 placeholder:text-[#8696a0]" />
                    <input type="text" value={row.description || ""} onChange={e => updateRow(si, ri, "description", e.target.value)} placeholder="Description (optional)" maxLength={MAX_ROW_DESC} className="w-full px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none focus:ring-1 focus:ring-teal-500/30 placeholder:text-[#8696a0]" />
                    <p className="text-[7px] text-[#8696a0] px-1">Row ID is set automatically by the backend.</p>
                  </div>
                  <button onClick={() => removeRow(si, ri)} className="p-0.5 text-[#667781] hover:text-red-500 mt-1"><Trash2 size={10} /></button>
                </div>
              ))}
              <button onClick={() => addRow(si)} disabled={totalRows >= MAX_TOTAL_ROWS} className="text-[9px] text-teal-600 font-semibold hover:underline disabled:opacity-30">+ Add Row</button>
            </div>
          ))}
          <button onClick={addSection} disabled={form.listSections.length >= MAX_SECTIONS} className="text-[10px] text-teal-600 font-bold hover:underline disabled:opacity-30 flex items-center gap-1"><Plus size={10} />Add Section</button>
          <p className="text-[8px] text-[#8696a0]">Rows: {totalRows}/{MAX_TOTAL_ROWS} • Sections: {form.listSections.length}/{MAX_SECTIONS}</p>
        </div>
      )}

      {/* Variable-based list */}
      {form.useVariable && (
        <div className="mb-3 bg-white rounded-lg shadow-sm p-2.5 space-y-2">
          <label className="text-[10px] font-bold text-[#667781] uppercase">Array Variable</label>
          <VariableDropdown value={form.listFromVariable} onChange={name => updateField("listFromVariable", name)} flowVariables={flowVariables||[]} allowCreate onCreateVariable={onCreateVariable} placeholder="Select array variable..." />
          <p className="text-[8px] text-[#8696a0]">Each element should have fields mapped below:</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2"><span className="text-[9px] font-semibold text-[#667781] w-16">Title</span><input type="text" value={form.listVariableFieldMap.title} onChange={e => { const m = { ...form.listVariableFieldMap, title: e.target.value }; updateField("listVariableFieldMap", m); }} placeholder="e.g. name" className="flex-1 px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none focus:ring-1 focus:ring-teal-500/30" /></div>
            <div className="flex items-center gap-2"><span className="text-[9px] font-semibold text-[#667781] w-16">Desc</span><input type="text" value={form.listVariableFieldMap.description} onChange={e => { const m = { ...form.listVariableFieldMap, description: e.target.value }; updateField("listVariableFieldMap", m); }} placeholder="e.g. desc (optional)" className="flex-1 px-2 py-1 text-[10px] bg-[#F0F0F0] rounded border-0 focus:outline-none focus:ring-1 focus:ring-teal-500/30" /></div>
            <p className="text-[7px] text-[#8696a0] px-1">Row ID is set automatically by the backend.</p>
          </div>
        </div>
      )}

      {/* Store selection */}
      <div className="mb-3">
        <label className="text-[10px] font-bold text-[#667781] uppercase tracking-wider mb-1 block">Store selection in variable</label>
        <VariableDropdown value={form.selectionVariable || ""} onChange={name => updateField("selectionVariable", name)} flowVariables={flowVariables||[]} allowCreate onCreateVariable={onCreateVariable} placeholder="Optional: save selection..." />
      </div>
      </NodeEditorTabs>

      <div className="flex gap-2">
        <button onClick={handleSave} className="flex-1 py-2 text-[12px] font-bold bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"><Check size={14} />Save</button>
        <button onClick={onCancel} className="flex-1 py-2 text-[12px] font-bold bg-white text-[#667781] rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">Cancel</button>
      </div>
    </div>
  );
};

/* ── Main Node ────────────────────────────────────────────────── */
const InteractiveListNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("interactiveList");

  const handleSave = useCallback((formData) => {
    if (data.onNodeDataChange) data.onNodeDataChange(id, { ...data, ...formData });
    closeEditor();
  }, [id, data, closeEditor]);

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle type="target" position={Position.Left} className="!w-3.5 !h-3.5 !bg-teal-500 !border-2 !border-white !shadow-sm" style={{ left: -7 }} />
      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor data={data} onSave={handleSave} presentation="fullscreen" onCancel={closeEditor} flowVariables={data.flowVariables || []} onCreateVariable={data.onCreateVariable} />
      </FlowNodeEditorShell>
      <Handle type="source" position={Position.Right} id="in_list" className="!w-3.5 !h-3.5 !bg-green-500 !border-2 !border-white !shadow-sm" style={{ top: "33%", right: -7 }} />
      <Handle type="source" position={Position.Right} id="not_in_list" className="!w-3.5 !h-3.5 !bg-red-500 !border-2 !border-white !shadow-sm" style={{ top: "67%", right: -7 }} />
      <div className="absolute -right-16 top-0 h-full flex flex-col justify-around items-start py-4 pointer-events-none">
        <span className="text-[7px] font-bold text-green-600 whitespace-nowrap">In List</span>
        <span className="text-[7px] font-bold text-red-600 whitespace-nowrap">Not In List</span>
      </div>
    </div>
  );
};

export default InteractiveListNode;
